import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";

// mui components
import { Box, IconButton, Stack, Tab, Tabs } from "@mui/material";

// custom components
import { TabPanel } from "../../tab-panel";
import PrimaryButton from "../../buttons/primary-button";
import Heading6 from "../../typography/heading-06";
import RequiredError from "../../required-error";
import BorrowerInformation from "../../loan-form-card/borrower-information";
import PropertyDetails from "../../loan-form-card/property-details";
import LoanOriginator from "../../loan-form-card/loan-originator";
import LoanProcessor from "../../loan-form-card/loan-processor";
import AdditionalLoanContact from "../../loan-form-card/additional-loan-contact";

// mui icons
import {
	AddOutlined,
	ArrowForwardOutlined,
	CloseOutlined,
} from "@mui/icons-material";

// api slice
import { useCreateLoanMutation } from "../../../features/create-loan/createLoanApiSlice";
import { useFetchAEEmailsMutation } from "../../../features/fetch-ae-emails/fetchAEEmailsApiSlice";
import { useCreateAdminLoanMutation } from "../../../features/admin/create-loan/createAdminLoanApiSlice";
import { useUpdateFieldsMutation } from "../../../features/update-fields/updateFieldsApiSlice";
import { useUpdateMilestoneFreeRolesMutation } from "../../../features/update-milestone-free-roles/updateMilestoneFreeRolesApiSlice";
import { useGetBrokerListByTitleBranchMutation } from "../../../features/broker-search-title-branch/brokerSearchTitleBranchApiSlice";
import { useGetOrganizationDetailsMutation } from "../../../features/organization-detail/organizationDetailApiSlice";
import { useCreateBorrowerPairMutation } from "../../../features/create-borrower-pair/createBorrowerPairApiSlice";

// reducer slice
import {
	selectUserDetails,
	selectSelectedOrganization,
	setLoEmail,
} from "../../../features/auth/authSlice";
import { setBrokerListReducer } from "../../../features/broker-list/brokerListSlice";
import { selectImpersonatedUser } from "../../../features/admin/impersonated-user/impersonatedUserSlice";

// custom modal
import LoaderModal from "../../modal/loader-modal";
import HelpTextModal from "../../modal/help-text-modal";
import CommanErrorModal from "../../modal/common-error-modal";
import { useErrorMessages } from "../../../utils/error-messages.constants";

// helper utils
import {
	requiredFields,
	requiredFieldsForAdditionalContact,
	requiredFieldsForManualLoanOriginator,
} from "../../../utils/requiredFields";
import { handleSetPropertyDetails } from "../../../utils/loan-submission/set-property-details";
import {
	selectedUser,
	selectedUserWithLoanChannel,
} from "../../../utils/common-helpers";
import {
	BROKER_ADMIN,
	LOAN_CHANNEL_CONSTANTS,
	TPO_SECONDARY,
} from "../../../utils/common-constants";
import { borrowerCitizenshipTypeOptions } from "../../../utils/select-options";
import { runSilentValidation } from "../../../utils/silent-validation";
import { getOriginatorCompanyName } from "../../../utils/originator-company-name";
import { buildTpoFileContactFieldValues } from "../../../utils/tpo-file-contacts";
import useRegisterLoanSection from "../../../hooks/useRegisterLoanSection";
// custom styles
import styles from "./index.module.scss";
import {
	buildDirectPathFieldValue,
	withDirectPathPayload,
} from "../../../utils/direct-path.constants";

export const CreateLoanApplication = ({
	setLoanApplicationStage,
	applicationData,
	setApplicationData,
	setLoanGuid,
	loanGuid,
	organizationName,
	childOrganizationName,
	currentStage,
	registerSectionHandler,
	directPath,
}) => {
	console.log("70 applicationData create:", applicationData);

	const dispatch = useDispatch();
	const userDetails = useSelector(selectUserDetails);
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const impersonatedUser = useSelector(selectImpersonatedUser);

	const { getErrorMessage } = useErrorMessages();

	const [createLoan, { isLoading: creatingLoan }] = useCreateLoanMutation();
	const [fetchAEEmails, { isLoading: isFetchingAEEmails }] = useFetchAEEmailsMutation();

	const [createAdminLoan, { isLoading: creatingAdminLoan }] =
		useCreateAdminLoanMutation();

	const [updateFields, { isLoading: isUpdatingFields }] =
		useUpdateFieldsMutation();

	const [updateMilestoneFreeRoles] = useUpdateMilestoneFreeRolesMutation();

	const [createBorrowerPair, { isLoading: creatingBorrowerPair }] =
		useCreateBorrowerPairMutation();

	const [loanApplicationData, setLoanApplicationData] = useState([
		{
			mailingAddressSameAsPresentIndicator: false,
		},
		{
			mailingAddressSameAsPresentIndicator: false,
		},
	]);

	const [foreignNationalIndex, setForeignNationalIndex] = useState([]);

	const [tabValue, setTabValue] = useState(0);

	const [propertyInfo, setPropertyInfo] = useState({});
	const [grossRent, setGrossRent] = useState("");

	const [homeownersInsurance, setHomeownersInsurance] = useState("");
	const [floodInsurance, setFloodInsurance] = useState("");
	const [propertyTaxes, setPropertyTaxes] = useState("");
	const [realEstateTaxes, setRealEstateTaxes] = useState("");
	const [hoaDues, setHoaDues] = useState("");

	const [getBrokerListByTitleBranch] = useGetBrokerListByTitleBranchMutation();
	const [getOrganizationDetails] = useGetOrganizationDetailsMutation();

	const [currentOrgName, setCurrentOrgName] = useState({});
	const [currentOrgDetails, setCurrentOrgDetails] = useState({});

	const [organizationDetails, setOrganizationDetails] = useState({});
	const [childOrganizationDetails, setChildOrganizationDetails] = useState({});

	const [loanOriginatorEmail, setLoanOriginatorEmail] = useState("");
	const [loanOriginatorId, setLoanOriginatorId] = useState("");
	const [organizationUsers, setOrganizationUsers] = useState([]);
	const [loanOriginator, setLoanOriginator] = useState({});

	const [organizationLicense, setOrganizationLicense] = useState("");

	const [loanProcessorEmail, setLoanProcessorEmail] = useState("");
	const [loanProcessorId, setLoanProcessorId] = useState("");
	const [organizationProcessor, setOrganizationProcessor] = useState([]);
	const [loanProcessor, setLoanProcessor] = useState({});

	const [additionalContact, setAdditionalContact] = useState({
		name: "",
		phone: "",
		email: "",
	});

	const [manualOriginator, setManualOriginator] = useState(false);
	const [originatorData, setOriginatorData] = useState({
		orgName: "",
		address: "",
		unitNumber: "",
		city: "",
		state: "",
		zipcode: "",
		orgNmls: "",
		orgStateLicense: "",
		originatorEmail: "",
		originatorFirstName: "",
		originatorLastName: "",
		originatorNmls: "",
		originatorStateLicense: "",
		originatorPhone: "",
	});

	const [helpText, setHelpText] = useState("");
	const [extraHelpText, setExtraHelpText] = useState("");
	const [helpTextOpen, setHelpTextOpen] = useState(false);

	const handleOpenHelpText = () => {
		setHelpTextOpen(true);
	};

	const handleCloseHelpText = () => {
		setHelpTextOpen(false);
	};

	useEffect(() => {
		console.log("125 application fee data:", applicationData);

		if (applicationData !== undefined) {
			setGrossRent(applicationData?.subjectPropertyGrossRentalIncomeAmount);

			handleSetPropertyDetails(
				applicationData,
				setHomeownersInsurance,
				setFloodInsurance,
				setPropertyTaxes,
				setRealEstateTaxes,
				setHoaDues
			);
		}
	}, [applicationData]);

	const isAdmin = userDetails?.user_roles?.includes("admin") === true;
	console.log("260 is admin:", isAdmin);

	const isInternalUser =
		userDetails?.user_roles?.includes("executive") ||
		userDetails?.user_roles?.includes("manager") ||
		userDetails?.user_roles?.includes("AE") ||
		userDetails?.user_roles?.includes("AM") ||
		userDetails?.user_roles?.includes("PA") ||
		userDetails?.user_roles?.includes("assistant");

	// For multi-org users, use the explicitly selected org; fall back to organizationName for single-org users
	const effectiveOrgName = selectedOrganization || organizationName;

	let brokerSearchOrgName;
	let includeChildren;

	if (
		selectedUser(userDetails, impersonatedUser)?.access_level ===
		TPO_SECONDARY ||
		selectedUser(userDetails, impersonatedUser)?.access_level === BROKER_ADMIN
	) {
		// brokerSearchOrgName = encodeURIComponent(effectiveOrgName);
		brokerSearchOrgName = effectiveOrgName;
		includeChildren = true;
	} else if (
		selectedUser(userDetails, impersonatedUser)?.child_org_name !== "" && !selectedOrganization
	) {
		// brokerSearchOrgName = encodeURIComponent(childOrganizationName);
		brokerSearchOrgName = childOrganizationName
		includeChildren = false;
	} else {
		// brokerSearchOrgName = encodeURIComponent(effectiveOrgName);
		brokerSearchOrgName = effectiveOrgName
		includeChildren = false;
	}

	const regexNdcOrCorr = /\b(?:NDC|CORR)\b/i;
	const isOrgNdcOrCorr = regexNdcOrCorr.test(brokerSearchOrgName);

	console.log("is organization NDC or CORR:", isOrgNdcOrCorr);

	const allowManualInput =
		isAdmin ||
		(isOrgNdcOrCorr &&
			(isInternalUser ||
				selectedUserWithLoanChannel(userDetails)?.loan_channel ===
				LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails)?.loan_channel ===
				LOAN_CHANNEL_CONSTANTS.CORR));

	useEffect(() => {
		const getBrokerListFunc = async () => {
			let searchResults = await getBrokerListByTitleBranch({
				orgName: brokerSearchOrgName,
				title: "Loan Officer",
				include_child: includeChildren,
			}).unwrap();

			dispatch(setBrokerListReducer(searchResults));

			console.log("150 loan origin:", searchResults);

			let updatedArr = searchResults.map((item) => ({
				...item,
				label: item?.user?.email || "",
				value: item?.user?.email || "",
			}));

			console.log("150 loan originator updated:", updatedArr);
			setOrganizationUsers(updatedArr);
		};

		const getProcessorListFunc = async () => {
			let searchResults = await getBrokerListByTitleBranch({
				orgName: brokerSearchOrgName,
				title: "Loan Processor",
				include_child: includeChildren,
			}).unwrap();

			let updatedArr = searchResults.map((item) => ({
				...item,
				label: item?.user?.email || "",
				value: item?.user?.email || "",
			}));

			console.log("150 loan processor updated:", updatedArr);
			setOrganizationProcessor(updatedArr);
		};

		const getOrganizationDetailsFunc = async () => {
			const searchResults = await getOrganizationDetails({
				organizationName: encodeURIComponent(effectiveOrgName),
			}).unwrap();
			setOrganizationDetails(searchResults);
		};

		const getChildOrganizationDetailsFunc = async () => {
			const searchResults = await getOrganizationDetails({
				organizationName: encodeURIComponent(childOrganizationName),
			}).unwrap();

			setChildOrganizationDetails(searchResults);
		};

		if (
			effectiveOrgName !== null &&
			effectiveOrgName !== undefined &&
			effectiveOrgName !== ""
		) {
			getBrokerListFunc();
			getProcessorListFunc();
			getOrganizationDetailsFunc();

			// Only fetch child organization details if no organization is explicitly selected
			// and the user has a default child organization
			if (
				!selectedOrganization &&
				selectedUser(userDetails, impersonatedUser)?.child_org_name !== ""
			) {
				getChildOrganizationDetailsFunc();
			}
		}
	}, [effectiveOrgName, selectedOrganization]);

	useEffect(() => {
		// Simple priority: selectedOrganization > childOrganizationName > organizationName
		const priorityOrgName = selectedOrganization || childOrganizationName || organizationName;
		setCurrentOrgName(priorityOrgName);
		setCurrentOrgDetails(organizationDetails);
	}, [
		organizationName,
		organizationDetails,
		childOrganizationName,
		selectedOrganization,
	]);

	useEffect(() => {
		if (
			loanOriginatorEmail && Object.keys(loanOriginatorEmail)?.length !== 0 ||
			loanOriginatorEmail !== ""
		) {
			const originatorDetails = organizationUsers?.find(
				(item) => item?.broker_id === loanOriginatorId
			);

			setLoanOriginator(originatorDetails);
		}
	}, [loanOriginatorEmail, loanOriginatorId]);

	useEffect(() => {
		if (
			loanProcessorEmail && Object.keys(loanProcessorEmail)?.length !== 0 ||
			loanProcessorEmail !== ""
		) {
			const processorDetails = organizationProcessor?.find(
				(item) => item?.broker_id === loanProcessorId
			);
			setLoanProcessor(processorDetails);
		}
	}, [loanProcessorEmail, loanProcessorId]);

	const [error, setError] = useState("");
	const [aeEmailErrors, setAeEmailErrors] = useState(null);
	const [commonErrorData, setCommonErrorData] = useState(null);
	const [commonErrorModalVisible, setCommonErrorModalVisible] = useState(false);

	const handleOpenCommonErrorModal = (errorKey) => {
		setCommonErrorData(getErrorMessage(errorKey));
		setCommonErrorModalVisible(true);
	};

	const handleCloseCommonErrorModal = () => {
		setCommonErrorModalVisible(false);
		setCommonErrorData(null);
	};

	const updateField = async (
		loanGuid,
		apiObj,
		shouldAdvance = true,
		aeEmailData,
		loanNumber
	) => {
		let salesRepName =
			manualOriginator &&
				(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
					selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
						?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
				? organizationDetails?.sales_rep_name
				: loanOriginator?.organization?.sales_rep_name;

		let valuesArr = [
			{ id: "TPO.X3", value: DateTime.now().toFormat("MM-dd-yyyy") },
			{ id: "ULDD.TotalMortgagedPropertiesCount", value: "1" },
			{
				id: "CX.PROPERTY.COMMERCIAL.UNITS",
				value: apiObj?.property?.financedNumberOfUnits || "",
			},
			{ id: "NEWHUD.X223", value: "" },
			{ id: "NEWHUD.X224", value: "" },
			{ id: "389", value: "" },
			{ id: "1620", value: "" },
			{ id: 315, value: getOriginatorCompanyName(apiObj, loanOriginator?.organization) },
			{ id: 313, value: loanOriginator?.organization?.city },
			{ id: 321, value: loanOriginator?.organization?.state },
			{ id: 323, value: loanOriginator?.organization?.zip },
			{ id: 3237, value: loanOriginator?.organization?.nmls_id },
			{ id: 1256, value: salesRepName },
			{ id: "CX.LC.AE.EMAIL", value: aeEmailData?.loan_processor_ae_email || aeEmailData?.branch_ae_email || aeEmailData?.company_ae_email || "" },
			{ id: "CX.LC.AE.EMAIL.COMPANY", value: aeEmailData?.company_ae_email || "" },
			{ id: "CX.LC.AE.EMAIL.LO", value: aeEmailData?.loan_processor_ae_email || "" },
			{ id: "TPO.X62", value:  loanOriginatorId},
			{ id: "TPO.X75", value:  loanProcessorId},
		];

		if (selectedUser(userDetails, impersonatedUser)?.child_org_name !== "") {
			valuesArr.push({
				id: "CX.LC.AE.EMAIL.BRANCH",
				value: aeEmailData?.branch_ae_email || "",
			});
		}

		console.log("300 foreign national:", foreignNationalIndex);

		if (
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR
		) {
			valuesArr.push({
				id: "CX.LOLP.SELECT.MANUAL.METHOD",
				value: manualOriginator ? "Y" : "N",
			});

			if (manualOriginator) {
				valuesArr.push({
					id: "315",
					value: originatorData?.orgName,
				});
				valuesArr.push({
					id: "313",
					value: originatorData?.city,
				});
				valuesArr.push({
					id: "321",
					value: originatorData?.state,
				});
				valuesArr.push({
					id: "323",
					value: originatorData?.zipcode,
				});
				valuesArr.push({
					id: "3237",
					value: originatorData?.orgNmls,
				});
			}
		}


		/*** Foreign National Citizenship parameter CX.CITIZENSHIP CX.FOREIGNNATL configuration START ***/

		// Initialize hasForeignNational as "No"
		var hasForeignNational = "No";

		// Loop through the range of indices (0 to 5) for 3 borrower pairs
		for (let index = 0; index < 6; index++) {
			// Create the id based on the index
			const id = `CX.FOREIGNNATLBORR${index + 1}`;

			// Set value to "Yes" if index is in foreignNationalIndex, otherwise "No"
			const value = foreignNationalIndex.includes(index) ? "Yes" : "No";

			// If any borrower is "Yes", update hasForeignNational
			if (value === "Yes") {
				hasForeignNational = "Yes";
			}

			// Push borrower-specific value
			valuesArr.push({ id, value });
		}

		// Push overall CX.FOREIGNNATL value
		valuesArr.push({
			id: "CX.FOREIGNNATL",
			value: hasForeignNational
		});

		const borrower1Citizenship = loanApplicationData[0]?.urla2020CitizenshipResidencyType;
		console.log("Borrower Details: " , borrower1Citizenship);
		const firstBorrowerCitizenShipType = borrowerCitizenshipTypeOptions.find(option => option.value === borrower1Citizenship);
		console.log("Borrower aa: " , firstBorrowerCitizenShipType);

		const foreignNotionalFirstBorrower = foreignNationalIndex.includes(0) ? "Yes" : "No";
		valuesArr.push({
			id: "CX.CITIZENSHIP",
			value: foreignNotionalFirstBorrower === "Yes" ? "Foreign National" : firstBorrowerCitizenShipType?.label
		});

		/***  Foreign National Citizenship parameter CX.CITIZENSHIP CX.FOREIGNNATL configuration END ***/	


		if (loanOriginatorEmail !== undefined && loanOriginatorEmail !== "") {
			valuesArr.push({
				id: "TPO.X67",
				value: loanOriginator?.cell_phone,
			});

			valuesArr.push({
				id: "2306",
				value: loanOriginator?.state_license || "",
			});
		}
		// Added blank values for below fields - LC 206
		valuesArr.push({
			id: "URLA.X189",
			value: "",
		});
		valuesArr.push({
			id: "URLA.X190",
			value: "",
		});

		const directPathFieldValue = buildDirectPathFieldValue(directPath);
		if (directPathFieldValue) {
			valuesArr.push(directPathFieldValue);
		}

		console.log("340 loan update values:", valuesArr);

		const updateFieldValues = await updateFields({
			loan_guid: loanGuid,
			field_values: valuesArr,
			selectedOrganization,
			userDetails,
		}).unwrap();
		console.log("90 file res:", updateFieldValues);

		if (updateFieldValues?.response === "Field values updated successfully.") {
			try {
				await updateMilestoneFreeRoles({
					loan_guid: loanGuid,
					loan_officer: {
						entityId: loanOriginatorId,
						email: loanOriginator?.user?.email || loanOriginatorEmail,
					},
					loan_processor: {
						entityId: loanProcessorId,
						email: loanProcessor?.user?.email || loanProcessorEmail,
					},
					account_executive: {
						entityId:
							aeEmailData?.identifiers?.loan_processor_sales_rep_entity_id ||
							loanOriginator?.organization?.sales_rep_id,
						email:
							aeEmailData?.loan_processor_ae_email ||
							aeEmailData?.ae_email ||
							"",
					},
				}).unwrap();
			} catch (err) {
				console.error("[Milestone Free Roles] Sync failed:", err);
			}			

			try {
				const isManualNdcOrCorr =
					manualOriginator &&
					(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
						?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
						selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
							?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR);

				const tpoFileContactValues = buildTpoFileContactFieldValues({
					aeEmailData,
					aeEntityId:
						aeEmailData?.identifiers?.loan_processor_sales_rep_entity_id ||
						loanOriginator?.organization?.sales_rep_id,
					organizationId: isManualNdcOrCorr
						? organizationDetails?.organization_id
						: loanOriginator?.organization?.organization_id,
				});

				if (tpoFileContactValues) {
					await updateFields({
						loan_guid: loanGuid,
						field_values: tpoFileContactValues,
						selectedOrganization,
						userDetails,
					}).unwrap();
				}
			} catch (err) {
				console.error("[TPO File Contacts] Sync failed:", err);
			}

			setError("");
			if (shouldAdvance) {
				window.scrollTo({
					top: 0,
					left: 0,
					behavior: "smooth",
				});
				setLoanApplicationStage(currentStage + 1);
			}
			return true;
		}

		setError(updateFieldValues?.response);
		return false;
	};

	const handleNext = async (shouldAdvance = true, skipValidation = false) => {
		setError("");

		if (
			!skipValidation &&
			!requiredFields(
				loanApplicationData,
				propertyInfo,
				grossRent,
				homeownersInsurance,
				propertyTaxes,
				loanOriginatorEmail,
				loanProcessorEmail,
				manualOriginator,
				originatorData,
				setError
			)
		) {
			return false;
		}

		if (
			!skipValidation &&
			manualOriginator &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			if (!requiredFieldsForManualLoanOriginator(originatorData, setError)) {
				return false;
			}
		}

		if (
			!skipValidation &&
			additionalContact?.name !== "" &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			if (!requiredFieldsForAdditionalContact(additionalContact, setError)) {
				return false;
			}
		}

		let borrowerPairData = [];

		console.log("445 loan applicationData:", loanApplicationData);

		let applications = [];

		for (let index = 0; index < loanApplicationData.length; index += 2) {
			if (
				loanApplicationData[index]?.urla2020CitizenshipResidencyType ===
				"Foreign National"
			) {
				loanApplicationData[index].urla2020CitizenshipResidencyType =
					"NonPermanentResidentAlien";
			}

			if (
				loanApplicationData[index + 1]?.urla2020CitizenshipResidencyType ===
				"Foreign National"
			) {
				loanApplicationData[index + 1].urla2020CitizenshipResidencyType =
					"NonPermanentResidentAlien";
			}

			applications.push([
				{
					borrower: loanApplicationData[index],
					coBorrower: loanApplicationData[index + 1],
				},
			]);

			borrowerPairData.push([
				{
					borrower: loanApplicationData[index],
					coborrower: loanApplicationData[index + 1],
				},
			]);
		}

		console.log("430 applicationData:", applicationData);
		console.log("430 borrower pair data;", borrowerPairData);

		// Update the monthly payment for the specified fee type
		let updatedFees = [];

		if (
			applicationData?.fees === undefined ||
			applicationData?.fees?.length === 0
		) {
			updatedFees.push({
				feeType: "FloodInsurance",
				monthlyPayment: floodInsurance,
			});
			updatedFees.push({
				feeType: "PropertyTaxes",
				monthlyPayment: propertyTaxes,
			});
		} else {
			applicationData?.fees?.map((fee) => {
				if (fee.feeType === "FloodInsurance") {
					updatedFees.push({ ...fee, monthlyPayment: floodInsurance });
				}

				if (fee.feeType === "PropertyTaxes") {
					updatedFees.push({ ...fee, monthlyPayment: propertyTaxes });
				}

				return fee;
			});
		}

		console.log("450 updated fees:", updatedFees);

		setApplicationData({
			...applicationData,
			subjectPropertyGrossRentalIncomeAmount: grossRent,
			proposedHazardInsuranceAmount: homeownersInsurance,
			proposedDuesAmount: hoaDues,
			fees: updatedFees,
			proposedRealEstateTaxesAmount:
				updatedFees?.find((item) => item?.feeType === "PropertyTaxes")
					?.monthlyPayment || "0",
			applications,
			property: propertyInfo,
			tPO: {
				lOEmail: loanOriginatorEmail,
				lPEmail: loanProcessorEmail,
			},
		});

		let tpoData = {};

		if (
			manualOriginator &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			tpoData["companyName"] = organizationDetails?.name;
			tpoData["companyId"] = organizationDetails?.company_id;
			tpoData["companyOrganizationId"] = organizationDetails?.organization_id;
			tpoData["companyLegalName"] = organizationDetails?.company_legal_name;
			tpoData["companyAddress"] = organizationDetails?.address;
			tpoData["companyCity"] = organizationDetails?.city;
			tpoData["companyState"] = organizationDetails?.state;
			tpoData["companyZip"] = organizationDetails?.zip;
			tpoData["companyPhone"] = organizationDetails?.phone;
			tpoData["companyAeName"] = organizationDetails?.sales_rep_name;
			tpoData["companyAeUserName"] = organizationDetails?.sales_rep_id;
		} else if (
			selectedUser(userDetails, impersonatedUser)?.child_org_name !== ""
		) {
			tpoData["companyName"] = organizationDetails?.name;
			tpoData["companyId"] = organizationDetails?.company_id;
			tpoData["companyOrganizationId"] = organizationDetails?.organization_id;
			tpoData["companyLegalName"] = organizationDetails?.company_legal_name;
			tpoData["companyAddress"] = organizationDetails?.address;
			tpoData["companyCity"] = organizationDetails?.city;
			tpoData["companyState"] = organizationDetails?.state;
			tpoData["companyZip"] = organizationDetails?.zip;
			tpoData["companyPhone"] = organizationDetails?.phone;
			tpoData["companyAeName"] = organizationDetails?.sales_rep_name;
			tpoData["companyAeUserName"] = organizationDetails?.sales_rep_id;
		} else {
			tpoData["companyName"] = loanOriginator?.organization?.name;
			tpoData["companyId"] = loanOriginator?.organization?.company_id;
			tpoData["companyOrganizationId"] =
				loanOriginator?.organization?.organization_id;
			tpoData["companyLegalName"] =
				loanOriginator?.organization?.company_legal_name;
			tpoData["companyAddress"] = loanOriginator?.organization?.address;
			tpoData["companyCity"] = loanOriginator?.organization?.city;
			tpoData["companyState"] = loanOriginator?.organization?.state;
			tpoData["companyZip"] = loanOriginator?.organization?.zip;
			tpoData["companyPhone"] = loanOriginator?.organization?.phone;
			tpoData["companyAeName"] = loanOriginator?.organization?.sales_rep_name;
			tpoData["companyAeUserName"] = loanOriginator?.organization?.sales_rep_id;
		}

		if (
			childOrganizationDetails ||
			selectedUser(userDetails, impersonatedUser)?.child_org_name !== ""
		) {
			tpoData["branchName"] = childOrganizationDetails?.name;
			tpoData["branchId"] = childOrganizationDetails?.company_id;
			tpoData["branchOrganizationId"] =
				childOrganizationDetails?.organization_id;
			tpoData["branchLegalName"] = childOrganizationDetails?.company_legal_name;
			tpoData["branchAddress"] = childOrganizationDetails?.address;
			tpoData["branchCity"] = childOrganizationDetails?.city;
			tpoData["branchState"] = childOrganizationDetails?.state;
			tpoData["branchZip"] = childOrganizationDetails?.zip;
			tpoData["branchPhone"] = childOrganizationDetails?.phone;
			tpoData["branchAeName"] = childOrganizationDetails?.sales_rep_name;
			tpoData["branchAeUserName"] = childOrganizationDetails?.sales_rep_id;
		}

		if (
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR
		) {
			tpoData["loEmail"] = additionalContact?.email;
			tpoData["loName"] = additionalContact?.name;
			tpoData["loBusinessPhone"] = additionalContact?.phone;
		} else {
			tpoData["loEmail"] = loanOriginator?.user?.email;
			tpoData["loStatus"] = loanOriginator?.login_enabled;
			tpoData["loAddress"] = loanOriginator?.address;
			tpoData["loCity"] = loanOriginator?.city;
			tpoData["loState"] = loanOriginator?.state;
			tpoData["loZip"] = loanOriginator?.zip_code;
			tpoData["loName"] =
				`${loanOriginator?.user?.first_name} ${loanOriginator?.user?.last_name}`;
			tpoData["loBusinessPhone"] = loanOriginator?.phone;
		}

		console.log("670 loanProcessor email:", loanProcessorEmail);
		console.log("670 loanProcessor:", loanProcessor);

		tpoData["lpEmail"] = loanProcessor?.user?.email;
		tpoData["lpStatus"] = loanProcessor?.login_enabled;
		tpoData["lpAddress"] = loanProcessor?.address;
		tpoData["lpCity"] = loanProcessor?.city;
		tpoData["lpState"] = loanProcessor?.state;
		tpoData["lpZip"] = loanProcessor?.zip_code;
		tpoData["lpName"] =
			`${loanProcessor?.user?.first_name} ${loanProcessor?.user?.last_name}`;
		tpoData["lpBusinessPhone"] = loanProcessor?.phone;

		console.log("550 tpoData:", tpoData);

		let originatorInfoData = {};

		if (
			manualOriginator &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			originatorInfoData["originatorAddressLineText"] = originatorData?.address;
			originatorInfoData["originatorAddressUnitIdentifier"] =
				originatorData?.unitNumber;
			originatorInfoData["interviewersCompanyStateLicense"] =
				originatorData?.orgStateLicense;
			originatorInfoData["interviewerEmail"] = originatorData?.originatorEmail;
			originatorInfoData["originatorFirstName"] =
				originatorData?.originatorFirstName;
			originatorInfoData["originatorLastName"] =
				originatorData?.originatorLastName;
			originatorInfoData["nmlsLoanOriginatorId"] =
				originatorData?.originatorNmls;
			originatorInfoData["interviewerLicenseIdentifier"] =
				originatorData?.originatorStateLicense;
			originatorInfoData["interviewerPhoneNumber"] =
				originatorData?.originatorPhone;
		} else {
			originatorInfoData["originatorAddressLineText"] =
				loanOriginator?.organization?.address;
			originatorInfoData["interviewerEmail"] = loanOriginator?.user?.email;
			originatorInfoData["interviewerName"] =
				`${loanOriginator?.user?.first_name} ${loanOriginator?.user?.last_name}`;
			originatorInfoData["originatorFirstName"] =
				loanOriginator?.user?.first_name;
			originatorInfoData["originatorLastName"] =
				loanOriginator?.user?.last_name;
			originatorInfoData["nmlsLoanOriginatorId"] = loanOriginator?.nmls_id;

			originatorInfoData["interviewerPhoneNumber"] = loanOriginator?.phone;
			originatorInfoData["interviewerLicenseIdentifier"] = "";
		}

		console.log("590 originatorInfoData:", originatorInfoData);

		let apiObj = {
			user_email: loanOriginatorEmail,
			loan_data: {
				...applicationData,
				subjectPropertyGrossRentalIncomeAmount: grossRent,
				proposedHazardInsuranceAmount: homeownersInsurance,
				proposedDuesAmount: hoaDues,
				fees: updatedFees,
				proposedRealEstateTaxesAmount:
					updatedFees?.find((item) => item?.feeType === "PropertyTaxes")
						?.monthlyPayment || "0",
				applications: applications[0],
				property: propertyInfo,
				tpo: {
					loAeName: loanOriginator?.organization?.sales_rep_id,
					...tpoData,
				},
				...originatorInfoData,
			},
		};

		console.log("265 api obj:", apiObj);

		// Step A: Fetch AE Emails — must succeed before loan creation
		let aeEmailData = null;
		try {
			setAeEmailErrors(null);
			const aeResult = await fetchAEEmails({
				broker_id: userDetails?.organizations?.find(org => org.name === selectedOrganization)?.tpo_broker_id || userDetails?.user_tpo_id || impersonatedUser?.user_tpo_id,
				loan_processor_broker_id: loanOriginatorId,
			}).unwrap();
			const hasAEEmail =
				aeResult?.ae_email ||
				aeResult?.branch_ae_email ||
				aeResult?.company_ae_email;
			if (!hasAEEmail) {
				handleOpenCommonErrorModal("AE_EMAIL_NOT_FOUND");
				return;
			}
			if (aeResult?.errors) {
				setAeEmailErrors(aeResult.errors);
				console.warn("[AE Email] Partial fetch errors:", aeResult.errors);
			}
			aeEmailData = aeResult;
		} catch (err) {
			console.error("[AE Email] Fetch failed:", err);
			handleOpenCommonErrorModal("AE_EMAIL_NOT_FOUND");
			return;
		}

		// Step B: Create Loan
		// if (loanOriginatorEmail !== undefined && loanOriginatorEmail !== "") {
		try {
			let fileUpload;

			const newApplicationNumber = (loanApplicationData[0]?.lastName + '-' + (loanApplicationData[0]?.mailingAddress?.urla2020StreetAddress ?? 'Unknown Address'));
			// Only check for old application number if updating an existing loan
			const oldApplicationNumber = (applicationData?.applications?.[0]?.borrower?.lastName + '-' + (applicationData?.applications?.[0]?.borrower?.mailingAddress?.urla2020StreetAddress ?? 'Unknown Address')) || newApplicationNumber;
			const hasApplicationNumberChanged = newApplicationNumber !== oldApplicationNumber;

			if (isAdmin) {
				fileUpload = await createAdminLoan(
					withDirectPathPayload(
						{
							...apiObj,
							loan_channel: impersonatedUser?.loan_channel,
							tpo_user_id: userDetails?.user_tpo_id,
							application_number: newApplicationNumber,
							...(hasApplicationNumberChanged && {
								old_application_number: oldApplicationNumber,
							}),
							manual_loan_creation: true,
							selectedOrganization,
							userDetails,
						},
						directPath
					)
				).unwrap();
			} else {
				fileUpload = await createLoan(
					withDirectPathPayload(
						{
							...apiObj?.loan_data,
							tpo_user_id: userDetails?.user_tpo_id,
							application_number: newApplicationNumber,
							...(hasApplicationNumberChanged && {
								old_application_number: oldApplicationNumber,
							}),
							manual_loan_creation: true,
							selectedOrganization,
							userDetails,
						},
						directPath
					)
				).unwrap();
			}

			console.log("90 create impersonated loan:", fileUpload);
			console.log("385 loan originator email:", loanOriginatorEmail);
			dispatch(setLoEmail(loanOriginatorEmail));
			setLoanGuid(fileUpload?.GUID);

			// create additional borrower pair
			if (loanApplicationData?.length >= 3) {
				let borrowerPair;

				console.log("575 borrower pair data:", borrowerPairData);

				// start index at 1 to skip the first pair which was used to create the loan
				for (let index = 1; index < borrowerPairData.length; index++) {
					borrowerPair = await createBorrowerPair({
						loanGuid: fileUpload?.GUID,
						data: { ...borrowerPairData[index][0] },
						selectedOrganization,
						userDetails,
					}).unwrap();
				}
				console.log("550 impersonated borrower pair data:", borrowerPair);
			}

			return updateField(
				fileUpload?.GUID,
				apiObj?.loan_data,
				shouldAdvance,
				aeEmailData,
				fileUpload?.Loan_Number
			);
		} catch (err) {
			console.log("550 err:", err);
			setError(err?.data?.details);
			return false;
		}
	};

	const validateSection = useCallback(() => {
		return runSilentValidation([
			(setError) =>
				requiredFields(
					loanApplicationData,
					propertyInfo,
					grossRent,
					homeownersInsurance,
					propertyTaxes,
					loanOriginatorEmail,
					loanProcessorEmail,
					manualOriginator,
					originatorData,
					setError
				),
			(setError) => {
				if (
					manualOriginator &&
					(selectedUserWithLoanChannel(
						userDetails,
						impersonatedUser,
						selectedOrganization
					)?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
						selectedUserWithLoanChannel(
							userDetails,
							impersonatedUser,
							selectedOrganization
						)?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
				) {
					return requiredFieldsForManualLoanOriginator(originatorData, setError);
				}

				return true;
			},
			(setError) => {
				if (
					additionalContact?.name !== "" &&
					(selectedUserWithLoanChannel(
						userDetails,
						impersonatedUser,
						selectedOrganization
					)?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
						selectedUserWithLoanChannel(
							userDetails,
							impersonatedUser,
							selectedOrganization
						)?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
				) {
					return requiredFieldsForAdditionalContact(additionalContact, setError);
				}

				return true;
			},
		]);
	}, [
		additionalContact,
		grossRent,
		homeownersInsurance,
		impersonatedUser,
		loanApplicationData,
		loanOriginatorEmail,
		loanProcessorEmail,
		manualOriginator,
		originatorData,
		propertyInfo,
		propertyTaxes,
		selectedOrganization,
		userDetails,
	]);

	const highlightSectionErrors = useCallback(() => {
		setError("");

		if (
			!requiredFields(
				loanApplicationData,
				propertyInfo,
				grossRent,
				homeownersInsurance,
				propertyTaxes,
				loanOriginatorEmail,
				loanProcessorEmail,
				manualOriginator,
				originatorData,
				setError
			)
		) {
			return;
		}

		if (
			manualOriginator &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			if (!requiredFieldsForManualLoanOriginator(originatorData, setError)) {
				return;
			}
		}

		if (
			additionalContact?.name !== "" &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			if (!requiredFieldsForAdditionalContact(additionalContact, setError)) {
				return;
			}
		}
	}, [
		additionalContact,
		grossRent,
		homeownersInsurance,
		impersonatedUser,
		loanApplicationData,
		loanOriginatorEmail,
		loanProcessorEmail,
		manualOriginator,
		originatorData,
		propertyInfo,
		propertyTaxes,
		selectedOrganization,
		userDetails,
	]);

	useRegisterLoanSection(registerSectionHandler, {
		validate: validateSection,
		save: () => handleNext(false, true),
		highlightErrors: highlightSectionErrors,
	});

	const handleAddBorrowerPair = () => {
		setLoanApplicationData([
			...loanApplicationData,
			{ mailingAddressSameAsPresentIndicator: false },
			{ mailingAddressSameAsPresentIndicator: false },
		]);
	};

	const handleRemoveBorrowerPair = () => {
		console.log("650 tab value:", tabValue);
		console.log("650 pair count:", loanApplicationData?.length / 2 - 1);

		if (tabValue === loanApplicationData?.length / 2 - 1) {
			setTabValue(tabValue - 1);
		}

		let newArr = loanApplicationData;

		// remove current selected borrower pair with following logic
		// tabvalue ~ loanApplicationData
		// 0 ~ 0, 1
		// 1 ~ 2, 3
		// 2 ~ 4, 5

		if (tabValue === 0) {
			newArr?.splice(tabValue, 2);
		} else {
			newArr?.splice(tabValue * 2, 2);
		}

		setLoanApplicationData([...newArr]);
	};

	function a11yProps(index) {
		return {
			id: `simple-tab-${index}`,
			"aria-controls": `simple-tabpanel-${index}`,
		};
	}

	const handleChangeTab = (event, newValue) => {
		setTabValue(newValue);
	};

	return (
		<Stack direction="column" className={styles.stackContainer}>
			<Stack className={styles.bodyContainer}>
				{/* tabs section */}
				<Tabs
					value={tabValue}
					onChange={handleChangeTab}
					aria-label="basic tabs example"
					TabIndicatorProps={{
						style: {
							backgroundColor: "#49787F",
						},
					}}
				>
					<Tab
						className={styles.tabBtnContainer}
						label={
							<Stack direction="row" className={styles.tabLabelContainer}>
								<Heading6
									text="Borrower Pair 1"
									fontType="semibold"
									extraClass={
										tabValue === 0 ? styles.activeTabText : styles.tabText
									}
								/>

								{tabValue === 0 && (
									<Stack
										className={styles.closeIconContainer}
										onClick={() =>
											loanApplicationData?.length > 2 &&
											handleRemoveBorrowerPair()
										}
									>
										<CloseOutlined className={styles.closeIcon} />
									</Stack>
								)}
							</Stack>
						}
						{...a11yProps(0)}
					/>
					{loanApplicationData?.length > 2 && (
						<Tab
							className={styles.tabBtnContainer}
							label={
								<Stack direction="row" className={styles.tabLabelContainer}>
									<Heading6
										text="Borrower Pair 2"
										fontType="semibold"
										extraClass={
											tabValue === 1 ? styles.activeTabText : styles.tabText
										}
									/>

									{tabValue === 1 && (
										<Stack
											className={styles.closeIconContainer}
											onClick={() => handleRemoveBorrowerPair()}
										>
											<CloseOutlined className={styles.closeIcon} />
										</Stack>
									)}
								</Stack>
							}
							{...a11yProps(1)}
						/>
					)}
					{loanApplicationData?.length > 4 && (
						<Tab
							className={styles.tabBtnContainer}
							label={
								<Stack direction="row" className={styles.tabLabelContainer}>
									<Heading6
										text="Borrower Pair 3"
										fontType="semibold"
										extraClass={
											tabValue === 2 ? styles.activeTabText : styles.tabText
										}
									/>

									{tabValue === 2 && (
										<Stack
											className={styles.closeIconContainer}
											onClick={() => handleRemoveBorrowerPair()}
										>
											<CloseOutlined className={styles.closeIcon} />
										</Stack>
									)}
								</Stack>
							}
							{...a11yProps(2)}
						/>
					)}

					<Box>
						<IconButton
							className={styles.addPairContainer}
							onClick={() =>
								loanApplicationData?.length < 6 && handleAddBorrowerPair()
							}
							disabled={loanApplicationData?.length > 4}
						>
							<AddOutlined className={styles.addPairIcon} />
						</IconButton>
					</Box>
				</Tabs>

				{/* borrower pair 1 */}
				{loanApplicationData?.length > 0 && (
					<TabPanel
						value={tabValue}
						index={0}
						className={tabValue === 0 ? styles.tabPanelContainer : ""}
					>
						{loanApplicationData?.slice(0, 2)?.map((borrower, index) => (
							<BorrowerInformation
								key={index}
								borrowerIndex={index}
								coBorrower={index % 2 !== 0}
								borrowerData={borrower}
								foreignNationalIndex={foreignNationalIndex}
								setForeignNationalIndex={setForeignNationalIndex}
								data={loanApplicationData}
								setData={setLoanApplicationData}
								error={error.includes("provide a value")}
							/>
						))}

						<PropertyDetails
							data={propertyInfo}
							setData={setPropertyInfo}
							loanData={applicationData}
							setLoanData={setApplicationData}
							error={error.includes("provide a value")}
							grossRent={grossRent}
							setGrossRent={setGrossRent}
							homeownersInsurance={homeownersInsurance}
							setHomeownersInsurance={setHomeownersInsurance}
							floodInsurance={floodInsurance}
							setFloodInsurance={setFloodInsurance}
							propertyTaxes={propertyTaxes}
							setPropertyTaxes={setPropertyTaxes}
							realEstateTaxes={realEstateTaxes}
							setRealEstateTaxes={setRealEstateTaxes}
							hoaDues={hoaDues}
							setHoaDues={setHoaDues}
							setHelpText={setHelpText}
							handleOpenHelpText={handleOpenHelpText}
						/>

						<LoanOriginator
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanOriginatorEmail={loanOriginatorEmail}
							setLoanOriginatorEmail={setLoanOriginatorEmail}
							loanOriginatorId={loanOriginatorId}
							setLoanOriginatorId={setLoanOriginatorId}
							organizationUsers={organizationUsers}
							loanOriginator={loanOriginator}
							setLoanOriginator={setLoanOriginator}
							setOrganizationLicense={setOrganizationLicense}
							propertyState={propertyInfo?.state}
							manualOriginator={manualOriginator}
							setManualOriginator={setManualOriginator}
							originatorData={originatorData}
							setOriginatorData={setOriginatorData}
							setHelpText={setHelpText}
							setExtraHelpText={setExtraHelpText}
							handleOpenHelpText={handleOpenHelpText}
							error={error.includes("provide a value")}
							allowManualInput={allowManualInput}
						/>

						<LoanProcessor
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanProcessorEmail={loanProcessorEmail}
							setLoanProcessorEmail={setLoanProcessorEmail}
							loanProcessorId={loanProcessorId}
							setLoanProcessorId={setLoanProcessorId}
							organizationProcessor={organizationProcessor}
							loanProcessor={loanProcessor}
							propertyState={propertyInfo?.state}
							error={error.includes("provide a value")}
						/>

						{(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
							?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
							selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
								?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR) && (
								<AdditionalLoanContact
									additionalContact={additionalContact}
									setAdditionalContact={setAdditionalContact}
									error={error.includes("provide a value")}
								/>
							)}
					</TabPanel>
				)}

				{/* borrower pair 2 */}
				{loanApplicationData?.length > 2 && (
					<TabPanel
						value={tabValue}
						index={1}
						className={tabValue === 1 ? styles.tabPanelContainer : ""}
					>
						{loanApplicationData?.slice(2, 4)?.map((borrower, index) => (
							<BorrowerInformation
								key={index}
								borrowerIndex={index + 2}
								coBorrower={index % 2 !== 0}
								borrowerData={borrower}
								foreignNationalIndex={foreignNationalIndex}
								setForeignNationalIndex={setForeignNationalIndex}
								data={loanApplicationData}
								setData={setLoanApplicationData}
								error={error.includes("provide a value")}
							/>
						))}

						<PropertyDetails
							data={propertyInfo}
							setData={setPropertyInfo}
							loanData={applicationData}
							setLoanData={setApplicationData}
							error={error.includes("provide a value")}
							grossRent={grossRent}
							setGrossRent={setGrossRent}
							homeownersInsurance={homeownersInsurance}
							setHomeownersInsurance={setHomeownersInsurance}
							floodInsurance={floodInsurance}
							setFloodInsurance={setFloodInsurance}
							propertyTaxes={propertyTaxes}
							setPropertyTaxes={setPropertyTaxes}
							realEstateTaxes={realEstateTaxes}
							setRealEstateTaxes={setRealEstateTaxes}
							hoaDues={hoaDues}
							setHoaDues={setHoaDues}
							setHelpText={setHelpText}
							handleOpenHelpText={handleOpenHelpText}
						/>

						<LoanOriginator
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanOriginatorEmail={loanOriginatorEmail}
							setLoanOriginatorEmail={setLoanOriginatorEmail}
							loanOriginatorId={loanOriginatorId}
							setLoanOriginatorId={setLoanOriginatorId}
							organizationUsers={organizationUsers}
							loanOriginator={loanOriginator}
							setLoanOriginator={setLoanOriginator}
							setOrganizationLicense={setOrganizationLicense}
							propertyState={propertyInfo?.state}
							manualOriginator={manualOriginator}
							setManualOriginator={setManualOriginator}
							originatorData={originatorData}
							setOriginatorData={setOriginatorData}
							setHelpText={setHelpText}
							setExtraHelpText={setExtraHelpText}
							handleOpenHelpText={handleOpenHelpText}
							error={error.includes("provide a value")}
							allowManualInput={allowManualInput}
						/>

						<LoanProcessor
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanProcessorEmail={loanProcessorEmail}
							setLoanProcessorEmail={setLoanProcessorEmail}
							loanProcessorId={loanProcessorId}
							setLoanProcessorId={setLoanProcessorId}
							organizationProcessor={organizationProcessor}
							loanProcessor={loanProcessor}
							propertyState={propertyInfo?.state}
							error={error.includes("provide a value")}
						/>

						{(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
							?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
							selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
								?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR) && (
								<AdditionalLoanContact
									additionalContact={additionalContact}
									setAdditionalContact={setAdditionalContact}
									error={error.includes("provide a value")}
								/>
							)}
					</TabPanel>
				)}

				{/* borrower pair 3 */}
				{loanApplicationData?.length > 4 && (
					<TabPanel
						value={tabValue}
						index={2}
						className={tabValue === 2 ? styles.tabPanelContainer : ""}
					>
						{loanApplicationData?.slice(4, 6)?.map((borrower, index = 4) => (
							<BorrowerInformation
								key={index}
								borrowerIndex={index + 4}
								coBorrower={index % 2 !== 0}
								borrowerData={borrower}
								foreignNationalIndex={foreignNationalIndex}
								setForeignNationalIndex={setForeignNationalIndex}
								data={loanApplicationData}
								setData={setLoanApplicationData}
								error={error.includes("provide a value")}
							/>
						))}

						<PropertyDetails
							data={propertyInfo}
							setData={setPropertyInfo}
							loanData={applicationData}
							setLoanData={setApplicationData}
							error={error.includes("provide a value")}
							grossRent={grossRent}
							setGrossRent={setGrossRent}
							homeownersInsurance={homeownersInsurance}
							setHomeownersInsurance={setHomeownersInsurance}
							floodInsurance={floodInsurance}
							setFloodInsurance={setFloodInsurance}
							propertyTaxes={propertyTaxes}
							setPropertyTaxes={setPropertyTaxes}
							realEstateTaxes={realEstateTaxes}
							setRealEstateTaxes={setRealEstateTaxes}
							hoaDues={hoaDues}
							setHoaDues={setHoaDues}
							setHelpText={setHelpText}
							handleOpenHelpText={handleOpenHelpText}
						/>

						<LoanOriginator
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanOriginatorEmail={loanOriginatorEmail}
							setLoanOriginatorEmail={setLoanOriginatorEmail}
							loanOriginatorId={loanOriginatorId}
							setLoanOriginatorId={setLoanOriginatorId}
							organizationUsers={organizationUsers}
							loanOriginator={loanOriginator}
							setLoanOriginator={setLoanOriginator}
							setOrganizationLicense={setOrganizationLicense}
							propertyState={propertyInfo?.state}
							manualOriginator={manualOriginator}
							setManualOriginator={setManualOriginator}
							originatorData={originatorData}
							setOriginatorData={setOriginatorData}
							setHelpText={setHelpText}
							setExtraHelpText={setExtraHelpText}
							handleOpenHelpText={handleOpenHelpText}
							error={error.includes("provide a value")}
							allowManualInput={allowManualInput}
						/>

						<LoanProcessor
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanProcessorEmail={loanProcessorEmail}
							setLoanProcessorEmail={setLoanProcessorEmail}
							loanProcessorId={loanProcessorId}
							setLoanProcessorId={setLoanProcessorId}
							organizationProcessor={organizationProcessor}
							loanProcessor={loanProcessor}
							propertyState={propertyInfo?.state}
							error={error.includes("provide a value")}
						/>

						{(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
							?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
							selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
								?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR) && (
								<AdditionalLoanContact
									additionalContact={additionalContact}
									setAdditionalContact={setAdditionalContact}
									error={error.includes("provide a value")}
								/>
							)}
					</TabPanel>
				)}
			</Stack>

			{/* loan flow error */}
			{error !== "" && (
				<RequiredError
					headerText={
						error.includes("provide a value") || error.includes("borrower.dependentCount")
							? "Missing fields"
							: "Error"
					}
					text={
						error.includes("Something went wrong")
							? "Updates Not Received by LOS. Please Try Again."
							: error.includes("provide a value")
								? "Please provide a value for all fields highlighted below before you continue."
								: error.includes("borrower.dependentCount")
									? "Please check your dependent count."
									: error
					}
				/>
			)}

			{aeEmailErrors && (
				<p style={{ color: "#f59e0b", fontSize: 12, marginTop: 4 }}>
					AE email lookup partially failed. Loan was created successfully.
				</p>
			)}

			<Stack direction="row" className={styles.footerContainer}>
				<PrimaryButton
					text="Save and Continue"
					endIcon={<ArrowForwardOutlined className={styles.nextIcon} />}
					onClick={() => handleNext()}
				/>
			</Stack>

			{/* modal */}
			<LoaderModal
				open={
					isFetchingAEEmails ||
					creatingLoan ||
					creatingAdminLoan ||
					isUpdatingFields ||
					creatingBorrowerPair
				}
			/>
			<HelpTextModal
				open={helpTextOpen}
				modalText={helpText}
				extraHelpText={extraHelpText}
				handleClose={handleCloseHelpText}
			/>
			<CommanErrorModal
				isOpen={commonErrorModalVisible}
				onClose={handleCloseCommonErrorModal}
				title={commonErrorData?.title}
				message={commonErrorData?.message}
				errorCode={commonErrorData?.code}
				errorResponse={commonErrorData?.error_response}
			/>
		</Stack>
	);
};

export default CreateLoanApplication;
